/**
 * CSV / XLSX export for the Plan page. CSV is zero-dependency; XLSX
 * dynamic-imports the `xlsx` library only when the user clicks the
 * Excel button so the main bundle isn't bloated by ~150KB.
 */

// Budget items are normalised before being passed here (estimatedMat + estimatedLabor).
const catTotal = (c) => (c.estimatedMat || 0) + (c.estimatedLabor || 0);

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapeCsvCell(value) {
  const s = String(value ?? "");
  if (/[",\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function isoDate() {
  return new Date().toISOString().slice(0, 10);
}

function buildRows(budget) {
  const header = ["Category", "Materials", "Labor/Svcs", "Total Planned", "Actual", "Variance", "Notes"];
  const body = budget.map((c) => {
    const mat = c.estimatedMat || 0;
    const labor = c.estimatedLabor || 0;
    const planned = mat + labor;
    return [c.name, mat, labor, planned, c.actual, planned - c.actual, c.notes || ""];
  });
  const totalMat   = budget.reduce((s, c) => s + (c.estimatedMat || 0), 0);
  const totalLabor = budget.reduce((s, c) => s + (c.estimatedLabor || 0), 0);
  const totalPlan  = totalMat + totalLabor;
  const totalAct   = budget.reduce((s, c) => s + (c.actual || 0), 0);
  const totals = ["TOTAL", totalMat, totalLabor, totalPlan, totalAct, totalPlan - totalAct, ""];
  return { header, body, totals };
}

export function exportBudgetCsv(budget) {
  const { header, body, totals } = buildRows(budget);
  const rows = [header, ...body, totals];
  const csv = rows.map((r) => r.map(escapeCsvCell).join(",")).join("\n");
  // BOM so Excel auto-detects UTF-8 (handles accented characters in notes).
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, `budget-${isoDate()}.csv`);
}

export async function exportBudgetXlsx(budget) {
  const XLSX = await import("xlsx");
  const { header, body, totals } = buildRows(budget);
  const data = [header, ...body, totals];
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws["!cols"] = [
    { wch: 40 }, // Category
    { wch: 12 }, // Type
    { wch: 14 }, // Materials
    { wch: 14 }, // Labor/Svcs
    { wch: 12 }, // Actual
    { wch: 12 }, // Variance
    { wch: 60 }, // Notes
  ];
  // Currency formatting on Planned/Actual/Variance columns (1..3) for every
  // body row + totals row.
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let R = 1; R <= range.e.r; R++) {
    for (let C = 2; C <= 5; C++) { // Materials, Labor/Svcs, Actual, Variance
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[addr];
      if (cell && typeof cell.v === "number") {
        cell.z = '"$"#,##0';
      }
    }
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Budget");
  XLSX.writeFile(wb, `budget-${isoDate()}.xlsx`);
}
